import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import {
  useCancelProgressInstructionMutation,
  useCompleteInstructionMutation,
  useLazyDownloadInstructionDocxQuery,
  useListProgressInstructionsQuery,
  useListProgressObjectsQuery,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  apiErrorMessage,
  saveBlob,
  useProgressPermission,
  type ProgressInstruction,
} from '@entities/progress';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { InstructionCard } from './instructions/InstructionCard';

/* Вкладка «Поручения» Руководителя: реестр выданных поручений по объектам с
   фильтром по статусу, документом, отметкой о выполнении и отменой. */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};

  & > select {
    width: auto;
    min-width: 200px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Empty = styled.div`
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(6)} 0`};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const STATUSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Все статусы' },
  { value: 'issued', label: 'Выданы' },
  { value: 'overdue', label: 'Просрочены' },
  { value: 'done', label: 'Выполнены' },
  { value: 'cancelled', label: 'Отменены' },
];

/** Роут `/agents/progress/instructions` — реестр поручений руководителя. */
export function ProgressInstructions() {
  const { can_read, can_write } = useProgressPermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetInstructionId = searchParams.get('id') ?? '';
  const { activeProjectId, setActiveProjectId } = useActiveProjectContext();
  const [projectId, setProjectId] = useState(
    () => searchParams.get('project') ?? (targetInstructionId ? '' : (activeProjectId ?? '')),
  );
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (!targetInstructionId && activeProjectId && !projectId) {
      setProjectId(activeProjectId);
    }
  }, [activeProjectId, projectId, targetInstructionId]);

  const objectsQ = useListProgressObjectsQuery(undefined, { skip: !can_read });
  const listQ = useListProgressInstructionsQuery(
    { project_id: projectId || undefined, status: status || undefined },
    { skip: !can_read },
  );
  const [download] = useLazyDownloadInstructionDocxQuery();
  const [complete] = useCompleteInstructionMutation();
  const [cancel] = useCancelProgressInstructionMutation();
  const [error, setError] = useState<string | null>(null);
  const items = useMemo<ProgressInstruction[]>(() => {
    const raw = listQ.data ?? [];
    if (targetInstructionId) {
      const matched = raw.filter((it) => it.id === targetInstructionId);
      if (matched.length > 0) return matched;
    }
    return raw;
  }, [listQ.data, targetInstructionId]);

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Руководитель».
      </FallbackBanner>
    );
  }

  async function run(action: () => Promise<unknown>, fallback: string) {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(apiErrorMessage(err) ?? fallback);
    }
  }

  return (
    <Page>
      <Filters>
        <Select
          value={projectId}
          onChange={(e) => {
            const v = e.target.value;
            setProjectId(v);
            setActiveProjectId(v || null);
          }}
          aria-label="Объект"
        >
          <option value="">Все объекты</option>
          {(objectsQ.data ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Статус">
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        {targetInstructionId && (
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('id');
              setSearchParams(next, { replace: true });
            }}
          >
            Сбросить фильтр поручения
          </Button>
        )}
      </Filters>
      {error && <Err role="alert">{error}</Err>}
      {listQ.isLoading ? (
        <Empty>Загрузка…</Empty>
      ) : items.length === 0 ? (
        <Empty>
          Поручений нет. Откройте дашборд объекта и нажмите «Поручение» на нужном показателе.
        </Empty>
      ) : (
        <List>
          {items.map((it) => (
            <InstructionCard
              key={it.id}
              item={it}
              perspective="issuer"
              actions={
                <>
                  <Button
                    type="button"
                    $variant="secondary"
                    $size="sm"
                    onClick={() =>
                      run(async () => {
                        const blob = await download(it.id).unwrap();
                        saveBlob(blob, `Поручение_${it.number}.docx`);
                      }, 'Не удалось скачать документ.')
                    }
                  >
                    Документ (DOCX)
                  </Button>
                  {can_write && it.status === 'issued' && (
                    <>
                      <Button
                        type="button"
                        $variant="secondary"
                        $size="sm"
                        onClick={() =>
                          run(
                            () => complete({ id: it.id, note: null }).unwrap(),
                            'Не удалось отметить выполнение.',
                          )
                        }
                      >
                        Отметить выполненным
                      </Button>
                      <Button
                        type="button"
                        $variant="ghost"
                        $size="sm"
                        onClick={() =>
                          run(() => cancel(it.id).unwrap(), 'Не удалось отменить поручение.')
                        }
                      >
                        Отменить
                      </Button>
                    </>
                  )}
                </>
              }
            />
          ))}
        </List>
      )}
    </Page>
  );
}
